import React, { useState, useEffect, useRef } from 'react';
import { Alert, Progress, Spinner } from 'reactstrap';
import { ebayApi } from '../../services/api';
import { POLLING_INTERVALS } from '../../utils/polling';

interface SyncStatus {
  status: 'idle' | 'syncing' | 'completed' | 'error';
  progress: number;
  message: string;
  total_products?: number;
  synced_products?: number;
  total_orders?: number;
  synced_orders?: number;
}

const INITIAL_SYNC_KEY = 'coderom_initial_sync_completed';

const SyncStatusBanner: React.FC = () => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [initialSyncDone, setInitialSyncDone] = useState(() => {
    return localStorage.getItem(INITIAL_SYNC_KEY) === 'true';
  });
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch sync status from API
  const fetchSyncStatus = async () => {
    try {
      const data = await ebayApi.getSyncStatus();
      setSyncStatus((prev) => {
        if (data.status && data.status !== 'idle') {
          // Only show banner if not dismissed, OR if this is a NEW sync starting
          const isNewSync = !prev || prev.status === 'idle' ||
                            (prev.status === 'completed' && data.status === 'syncing');
          if (isNewSync) {
            setDismissed(false);
          }
          return data;
        } else if (prev?.status === 'syncing' && data.status === 'idle') {
          // Sync just finished but status was cleared
          return null;
        } else if (data.status === 'idle') {
          return null;
        }
        return prev;
      });
    } catch (error) {
      // Ignore errors - user might not be logged in
    }
  };

  // Setup polling with dynamic interval
  useEffect(() => {
    // Initial fetch
    fetchSyncStatus();

    // Dynamic polling interval:
    // - 5 seconds when syncing (for real-time progress, optimized from 2s)
    // - 60 seconds when idle/completed (optimized from 30s)
    const setupInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      const isSyncing = syncStatus?.status === 'syncing';
      const pollInterval = isSyncing ? POLLING_INTERVALS.SYNC_STATUS_ACTIVE : POLLING_INTERVALS.SYNC_STATUS_IDLE;

      intervalRef.current = setInterval(() => {
        if (!dismissed || isSyncing) {
          fetchSyncStatus();
        }
      }, pollInterval);
    };

    setupInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [syncStatus?.status, dismissed]);

  // Auto-dismiss after 10 seconds when completed & mark initial sync as done
  useEffect(() => {
    if (syncStatus?.status === 'completed') {
      const timeout = setTimeout(() => {
        // Mark initial sync as completed AFTER showing the success message
        if (!initialSyncDone) {
          localStorage.setItem(INITIAL_SYNC_KEY, 'true');
          setInitialSyncDone(true);
        }
        setDismissed(true);
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [syncStatus?.status, initialSyncDone]);

  // Don't show if:
  // - Already dismissed
  // - No sync status
  // - Idle status
  // - Initial sync already completed (background syncs don't show notification)
  if (dismissed || !syncStatus || syncStatus.status === 'idle' || initialSyncDone) {
    return null;
  }

  const getAlertColor = () => {
    switch (syncStatus.status) {
      case 'syncing':
        return 'info';
      case 'completed':
        return 'success';
      case 'error':
        return 'danger';
      default:
        return 'info';
    }
  };

  const getIcon = () => {
    switch (syncStatus.status) {
      case 'syncing':
        return <Spinner size="sm" className="me-2" />;
      case 'completed':
        return <i className="ri-checkbox-circle-fill me-2"></i>;
      case 'error':
        return <i className="ri-error-warning-fill me-2"></i>;
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 9999,
        maxWidth: '400px',
        minWidth: '320px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Alert
        color={getAlertColor()}
        className="mb-0"
        isOpen={!dismissed}
      >
        {/* Close button */}
        <button
          type="button"
          className="btn-close"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            zIndex: 10,
            padding: '8px',
            opacity: 0.7,
          }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDismissed(true);
          }}
          aria-label="Close"
        />
        <div className="d-flex align-items-center mb-2">
          {getIcon()}
          <strong>
            {syncStatus.status === 'syncing' && 'Syncing your eBay data...'}
            {syncStatus.status === 'completed' && 'Sync Complete!'}
            {syncStatus.status === 'error' && 'Sync Error'}
          </strong>
        </div>

        <p className="mb-2 small">{syncStatus.message}</p>

        {syncStatus.status === 'syncing' && (
          <>
            <Progress
              value={syncStatus.progress}
              className="mb-2"
              style={{ height: '6px' }}
              color={syncStatus.progress < 70 ? 'info' : 'success'}
            />
            <div className="d-flex justify-content-between small text-muted">
              <span>
                {syncStatus.synced_products?.toLocaleString() || 0} /{' '}
                {syncStatus.total_products?.toLocaleString() || '?'} products
              </span>
              <span>{syncStatus.progress}%</span>
            </div>
          </>
        )}

        {syncStatus.status === 'completed' && (
          <div className="small">
            <i className="ri-shopping-bag-line me-1"></i>
            {syncStatus.synced_products?.toLocaleString()} products,{' '}
            <i className="ri-file-list-3-line me-1"></i>
            {syncStatus.synced_orders?.toLocaleString()} orders synced
          </div>
        )}
      </Alert>
    </div>
  );
};

export default SyncStatusBanner;
